// Member questions in Vault Questions (founder 2026-11-03: "The notification
// panel alert will suffice for now"). A Club member's Daily Question waits
// here for review; the bell (backend utils/dailyQuestionReviewNotice, type
// member_question_review) opens the ones still waiting. Pure, for the tests.

import { formatEt } from "../User/introPauseUtils";

// The tier drop-down's values (VaultQuestionsListLayer), "" = All Tier.
export const TIER_VALUES = ["", "iceBreaker", "deeperDive", "wildCard", "dailyQuestion", "dailyQuestion:asked", "keptTogether", "gatekeeper"];
const REVIEW_STATUS_VALUES = ["approved", "pending", "rejected"];

/**
 * The list's filters from the address (?status, ?isPublished, and since
 * 2026-11-03 ?tier and ?reviewStatus). Same as before for status and
 * isPublished. tier: only a value the drop-down has ("all" = All Tier);
 * left out, the tier the admin picked stays. reviewStatus: a human decline
 * keeps status pending_review (backend humanReviewUpdate sets only
 * reviewStatus), so "waiting for review" also needs reviewStatus=pending.
 * A link to an address with any filter (the bell, a Dashboard card) starts
 * on page 1, so it never lands on an empty later page. Back or Forward
 * (navigationType "POP", react-router useNavigationType) keeps the page the
 * admin was on: back from a question's View page lands where they were, as
 * before 2026-11-03 (review 2026-11-03).
 */
export const listFiltersFromSearch = (search, { navigationType } = {}) => {
  const params = new URLSearchParams(search || "");
  let statusFilter = params.get("status") || "all";
  // The pending filter from the address includes both keys.
  if (statusFilter === "pending_ai_review") statusFilter = "pending_ai_review,pending_review";
  const reviewStatus = params.get("reviewStatus");
  const filters = {
    statusFilter,
    isPublishedFilter: params.get("isPublished") || "all",
    reviewStatusFilter: REVIEW_STATUS_VALUES.includes(reviewStatus) ? reviewStatus : "all",
  };
  if (params.has("tier")) {
    const tier = params.get("tier") === "all" ? "" : params.get("tier");
    if (TIER_VALUES.includes(tier)) filters.tierFilter = tier;
  }
  if (navigationType !== "POP" && ["status", "isPublished", "tier", "reviewStatus"].some((k) => params.has(k))) filters.currentPage = 1;
  return filters;
};

/**
 * The Status drop-down's value for the filters in the store. Its "Pending
 * AI Review" sends both pending keys (pending_ai_review,pending_review), and
 * the Dashboard's Pending Review card sends pending_review alone: all three
 * read "Pending AI Review". (Before 2026-11-03 the two keys together, which
 * the drop-down itself sets, read "All Vault".)
 */
export const statusDropdownValue = ({ statusFilter, isPublishedFilter } = {}) => {
  if (isPublishedFilter === "true") return "published";
  if (statusFilter === "ai_approved") return "ai_approved";
  if (["pending_ai_review", "pending_review", "pending_ai_review,pending_review"].includes(statusFilter)) return "pending_ai_review";
  if (statusFilter === "ai_rejected") return "ai_rejected";
  return "all";
};

// The bell row for a member question waiting for review.
export const MEMBER_QUESTION_ALERT_TYPE = "member_question_review";
// Daily Questions, member submitted, not reviewed yet (a declined one is
// out). status=pending_ai_review is both pending keys, what the Status
// drop-down's "Pending AI Review" means everywhere else, so a switch of Tier
// after the bell keeps meaning what the drop-down says; a member question is
// always pending_review until reviewed (backend utils/dailyQuestionAsk), so
// the bell's list is the same rows.
export const MEMBER_QUESTIONS_WAITING_PATH = "/vault-questions?tier=dailyQuestion:asked&status=pending_ai_review&reviewStatus=pending";
// A test account's question is not in "member submitted" (backend
// tierFilterFor), so a bell row for test questions only opens every tier,
// where it shows tagged "test account question".
export const TEST_QUESTIONS_WAITING_PATH = "/vault-questions?tier=all&status=pending_ai_review&reviewStatus=pending";

/** Where a click on the bell's member question row goes (the whole group). */
export const memberQuestionBellPath = (n) => {
  const rows = [n, ...(Array.isArray(n?.subNotifications) ? n.subNotifications : [])].filter(Boolean);
  const onlyTest = rows.length > 0 && rows.every((r) => String(r?.title || "").startsWith("[Test]"));
  return onlyTest ? TEST_QUESTIONS_WAITING_PATH : MEMBER_QUESTIONS_WAITING_PATH;
};

// WHO ASKED IT (2026-11-03). The backend's list and view now send, on a
// member-asked row only: askedByFirstName (or null), askedByIdTail (last 6
// of their id), askedPocketName (or null), askedAt, and waitingDays (whole
// days since it was asked while reviewStatus is pending, else null). Never
// the email. A server without them still draws the id tail and the time.

const MEMBER_ID = /^[a-f0-9]{24}$/;

/** "Nicole (..1b143b)", or "A member (..1b143b)" with no usable name. */
export const askerName = (item) => {
  if (!item || !item.askedBy) return "";
  const tail = item.askedByIdTail || String(item.askedBy).slice(-6);
  return `${item.askedByFirstName || "A member"} (..${tail})`;
};

// A DELETED ASKER (review 2026-11-03). Deleting an account keeps its name,
// and the member's Admin page answers 404 for it (backend user_details),
// which View User draws as a blank page. The backend marks such a row
// askedByDeleted: true (withAskerFields; until it does, nothing changes
// here): the name is then plain text, no link, with ", deleted account".

/** The member's Admin page (the route the bell uses), or null. */
export const askerPagePath = (item) => {
  const id = item && item.askedBy ? String(item.askedBy) : "";
  if (item && item.askedByDeleted === true) return null;
  return MEMBER_ID.test(id) ? `/user/view-user/${id}` : null;
};

/** "Nicole (..1b143b)", or "Nicole (..1b143b), deleted account". */
export const askerLabel = (item) => {
  const name = askerName(item);
  return name && item.askedByDeleted === true ? `${name}, deleted account` : name;
};

// 🔴 THE NOTE A MEMBER QUESTION WAS SAVED WITH IS NOT TRUE (review
// 2026-11-03). The backend (utils/dailyQuestionAsk) stores "Asked by a member
// (Ask the Daily Question). Publish to run it; the card never shows a name."
// on every real member's question, Nicole's included. Publish does not run
// it: the Publish button is off while it waits, the publish endpoint only
// sets isPublished, and the drop picks by isActive, which the approval (green
// check) or an AI approval sets. So that note is never shown; any other note
// (a test account's, or one an admin wrote) shows as written.
const UNTRUE_MEMBER_NOTE = /Publish to run it/i;

/** The question's internal note to show, or "". */
export const internalNoteLine = (item) => {
  const note = String((item && item.notesInternal) || "").trim();
  return note && !UNTRUE_MEMBER_NOTE.test(note) ? note : "";
};

/** "Asked Oct 18, 11:11 PM ET", or "" with no time. */
export const askedAtLine = (item) => {
  const when = formatEt(item && item.askedAt);
  return when ? `Asked ${when}` : "";
};

/**
 * While it waits for review: { text: "Waiting 16 days", late }, late (red)
 * from 2 days. null once reviewed, or from a server that does not say.
 */
export const waitingLine = (item) => {
  const days = item ? item.waitingDays : null;
  if (typeof days !== "number" || !Number.isFinite(days) || days < 0) return null;
  const text = days < 1 ? "Waiting less than a day" : days === 1 ? "Waiting 1 day" : `Waiting ${days} days`;
  return { text, late: days >= 2 };
};

// THE REVIEW BOXES (2026-11-03). A declined member question shows the reason
// on the member's You tab (backend utils/dailyQuestionAsk mine: "not this
// time" with the reviewNote of a human decline), so the box says so; a bank
// question has nobody to see it.
export const declineReasonPlaceholder = (item) =>
  item && item.askedBy ? "Reason (optional). The member sees this reason on their You tab." : "Reason (optional, kept with the question)";

// A member question's approve reply (backend approveReplyMessage) says which
// evening it runs and whether the member was told, with askerNotice as data.
// It is shown in a dialog the admin closes: the banner is gone in a second.
export const isMemberApprovalReply = (data) => !!(data && data.status === 1 && data.askerNotice && data.message);
